import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AangifteSchenkbelasting,
    APV,
    BokkzAlgDataService,
    BokkzSbDataService,
    BokkzSbLogic,
    Effecten,
    GiftTaxEntity,
    IntermediaryEntity,
    NotarieleAkte,
    OmschrijvingEnBedrag,
    OndernemingSB,
    OndernemingSBRechtsvorm,
    OnroerendeZaakSB,
    Opvolger,
    PU,
    RoerendeZaak,
    Schenking,
    SchenkingOpPapier,
    SchenkingRelatieBegiftigdeMetSchenker,
    Vrijstellingen,
    Vruchtgebruik
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { NgIf } from '@angular/common';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { Subscription } from 'rxjs';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-ondernemingen',
    standalone: true,
    imports: [
        NgIf,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiTooltipModule
    ],
    templateUrl: './ondernemingen.component.html',
    styleUrl: './ondernemingen.component.css'
})
export class OndernemingenComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    private giftTaxEntitySubscription: Subscription | undefined;
    private sbBerekeningSubscription: Subscription | undefined;
    public intermediary = {} as IntermediaryEntity;
    public giftTaxEntity = {} as GiftTaxEntity;
    public sbAangifte = {} as AangifteSchenkbelasting;
    public schenkingFg = {} as FormGroup;
    public ondernemingFg = {} as FormGroup;
    public recapitulatieFg = {} as FormGroup;
    public focusedElementId: string | null = null;

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' }
    ];

    public optionsRechtsvorm: SelectFormControlItem<string>[] = [
        { value: 'BV', label: 'Besloten vennootschap (BV)' },
        { value: 'NV', label: 'Naamloze vennootschap (NV)' },
        { value: 'CV', label: 'Commanditaire vennootschap (CV)' },
        { value: 'EENMANSZAAK', label: 'Eenmanszaak' },
        { value: 'MAATSCHAP', label: 'Maatschap' },
        { value: 'VOF', label: 'Vennootschap onder firma (VOF)' },
        { value: 'OVERIG', label: 'Overig' }
    ];

    constructor(
        public algDataService: BokkzAlgDataService,
        public dataService: BokkzSbDataService,
        public sbService: BokkzSbLogic,
        private fb: FormBuilder) {
        this.schenkingFg = this.fb.group<Schenking>({
            datumSchenking: null as unknown as Date,
            relatieBegiftigdeMetSchenker: null as unknown as SchenkingRelatieBegiftigdeMetSchenker,
            herroepelijkeSchenking: null as unknown as boolean,
            schenkerBetaaltDeSchenkbelasting: null as unknown as boolean,
            uitbreidingBegripSchenker: null as unknown as boolean,
            contanten: null as unknown as OmschrijvingEnBedrag,
            effecten: null as unknown as Effecten,
            onroerendeZaken: null as unknown as OnroerendeZaakSB,
            ondernemingen: null as unknown as OndernemingSB,
            apv: null as unknown as APV,
            periodiekeUitkering: null as unknown as PU,
            roerendeZaak: null as unknown as RoerendeZaak,
            schuldigerkenning: null as unknown as SchenkingOpPapier,
            kwijtscheldingSchuld: null as unknown as OmschrijvingEnBedrag,
            vruchtgebruik: null as unknown as Vruchtgebruik,
            overige: null as unknown as OmschrijvingEnBedrag,
            vrijstellingen: null as unknown as Vrijstellingen,
            notarieleAkte: null as unknown as NotarieleAkte,
            belastingBetaaldInAnderLand: null as unknown as number,
            landCodeBetaaldInAnderLand: null as unknown as string
        });
        this.ondernemingFg = this.fb.group({
            rsinOnderneming: null as unknown as string,
            naamOnderneming: null as unknown as string,
            rechtsvorm: null as unknown as OndernemingSBRechtsvorm,
            goingConcernWaarde: null as unknown as number,
            liquidatieWaarde: null as unknown as number,
            waardeOndernemingsVermogen: null as unknown as number,
            waardeBeleggingsVermogen: null as unknown as number,
            stilleReservesInOndernemingsVermogen: null as unknown as number,
            oorspronkelijkeVerkrijgingsprijs: null as unknown as number,
            latenteInkomstenBelasting: null as unknown as number,
            voorOverlegHeeftPlaatsgevonden: false,
            verzoekToepassenBorInIb: false,
            verzoekToepassenBorInSw: false,
            aandeelInOnderneming: null as unknown as number,
            verkrijgingsPrijs: null as unknown as number,
            opvolger: this.fb.group<Opvolger>({
                protocolNummerNotaris: null as unknown as string,
                beconnummer: null as unknown as string,
                bsn: null as unknown as string,
                voorletters: null as unknown as string,
                tussenvoegsel: null as unknown as string,
                achternaam: null as unknown as string,
                geboortedatum: null as unknown as Date,
                instellingnaam: null as unknown as string,
                verzoekConserverendeAanslag: false,
                waardeTegenPrestatie: null as unknown as number
            }),
        });
        this.recapitulatieFg = this.fb.group({
            waardeVanDeOnderneming: null as unknown as number,
            koopsom: null as unknown as number,
            latenteBelasting: null as unknown as number,
            belasteWaardeVanDeOnderneming: null as unknown as number
        });
    }

    ngOnInit(): void {
        this.giftTaxEntitySubscription = this.dataService.giftTaxEntity.subscribe((value) => {
            this.giftTaxEntity = value;
            this.sbAangifte = value.aangifte!;
            if (value.aangifte!.schenking) {
                this.schenkingFg.patchValue(value.aangifte!.schenking[0], { emitEvent: false });
                if (value.aangifte!.schenking[0] && value.aangifte!.schenking[0].ondernemingen) {
                    this.ondernemingFg.patchValue(value.aangifte!.schenking[0].ondernemingen, { emitEvent: false });
                    this.recapitulatieFg.patchValue({ waardeVanDeOnderneming: Math.max(this.ondernemingFg.get('goingConcernWaarde')?.value || 0, this.ondernemingFg.get('liquidatieWaarde')?.value || 0) }, { emitEvent: false });
                    this.recapitulatieFg.patchValue({ koopsom: value.aangifte!.schenking[0].ondernemingen.opvolger.waardeTegenPrestatie }, { emitEvent: false });
                    if (!value.aangifte?.schenking[0].ondernemingen.verzoekToepassenBorInIb) {
                        this.recapitulatieFg.patchValue({ latenteBelasting: value.aangifte?.schenking[0].ondernemingen.latenteInkomstenBelasting }, { emitEvent: false });
                    }
                }
            }
        });
        this.sbBerekeningSubscription = this.dataService.sbBerekening.subscribe((value) => {
            this.recapitulatieFg.patchValue({ belasteWaardeVanDeOnderneming: value.totaalGeschonkenOndernemingsVermogen }, { emitEvent: false });
            if (value.berekendeOndernemingen && value.berekendeOndernemingen[0]) {
                if (value.berekendeOndernemingen[0].berekeningPerOpvolger) {
                    this.recapitulatieFg.patchValue({ latenteBelasting: value.berekendeOndernemingen[0].berekeningPerOpvolger[0].aandeelLatenteInkomstenbelasting }, { emitEvent: false });
                }
                this.recapitulatieFg.patchValue({ belasteWaardeVanDeOnderneming: value.totaalGeschonkenOndernemingsVermogen }, { emitEvent: false });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.giftTaxEntitySubscription) {
            this.giftTaxEntitySubscription.unsubscribe();
        }
        if (this.sbBerekeningSubscription) {
            this.sbBerekeningSubscription.unsubscribe();
        }
    }

    saveForm(): void {
        this.ondernemingFg.updateValueAndValidity({ emitEvent: false });
        this.schenkingFg.updateValueAndValidity({ emitEvent: false });
        const schenking = this.schenkingFg.value;
        schenking.ondernemingen = {
            rsinOnderneming: this.ondernemingFg.value.rsinOnderneming,
            adresOnderneming: this.ondernemingFg.value.adresOnderneming,
            naamOnderneming: this.ondernemingFg.value.naamOnderneming,
            rechtsvorm: this.ondernemingFg.value.rechtsvorm,
            goingConcernWaarde: this.ondernemingFg.value.goingConcernWaarde,
            liquidatieWaarde: this.ondernemingFg.value.liquidatieWaarde,
            waardeOndernemingsVermogen: this.ondernemingFg.value.waardeOndernemingsVermogen,
            waardeBeleggingsVermogen: this.ondernemingFg.value.waardeBeleggingsVermogen,
            stilleReservesInOndernemingsVermogen: this.ondernemingFg.value.stilleReservesInOndernemingsVermogen,
            latenteInkomstenBelasting: this.ondernemingFg.value.latenteInkomstenBelasting,
            voorOverlegHeeftPlaatsgevonden: this.ondernemingFg.value.voorOverlegHeeftPlaatsgevonden,
            verzoekToepassenBorInIb: this.ondernemingFg.value.verzoekToepassenBorInIb,
            verzoekToepassenBorInSw: this.ondernemingFg.value.verzoekToepassenBorInSw,
            aandeelInOnderneming: this.ondernemingFg.value.aandeelInOnderneming,
            oorspronkelijkeVerkrijgingsprijs: this.ondernemingFg.value.oorspronkelijkeVerkrijgingsprijs,
            opvolger: this.ondernemingFg.value.opvolger
        };
        if (schenking.ondernemingen.aandeelInOnderneming != undefined) {
            schenking.ondernemingen.opvolger.tellerOvergedragenAandeel = schenking.ondernemingen.aandeelInOnderneming * 100;
            schenking.ondernemingen.opvolger.noemerOvergedragenAandeel = 100 * 100;
        }
        this.giftTaxEntity.aangifte = {
            ...this.sbAangifte,
            schenking: [schenking]
        } as AangifteSchenkbelasting;
        this.dataService.updateGiftTaxEntity(this.giftTaxEntity);
        setTimeout(() => {
            if (schenking.ondernemingen.goingConcernWaarde != undefined
                && schenking.ondernemingen.aandeelInOnderneming != undefined) {
                this.sbService.getCalculation();
            }
        }, 1500);
    }

    getNaamSchenker() {
        const aangever = this.giftTaxEntity.aangifte?.schenker;
        if (!aangever) return null;

        const voorletters = aangever.voorletters?.replace(/\./g, '') || '';
        const tussenvoegsel = aangever.tussenvoegsels || '';
        const achternaam = aangever.achternaam || '';

        // Filter lege strings en voeg netjes samen
        return [voorletters, tussenvoegsel, achternaam]
            .filter(onderdeel => onderdeel.trim() !== '')
            .join(' ');
    }
}
